import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase';
import './EmailRegistrationCard.css';

const EmailRegistrationCard = ({ 
    user, 
    posthog, 
    onStravaConnect = null, 
    variant = 'default',
    style = {},
    onRewardClaimed = null,
    alwaysVisible = false // New prop to override 5-minute hiding
}) => {
    const { t } = useTranslation();
    const [emailRewardStatus, setEmailRewardStatus] = useState({ claimed: false, email: null });
    const [isLoadingEmailStatus, setIsLoadingEmailStatus] = useState(true);
    const [emailForCredits, setEmailForCredits] = useState('');
    const [isSubmittingEmailForCredits, setIsSubmittingEmailForCredits] = useState(false);
    const [emailCreditsSuccess, setEmailCreditsSuccess] = useState(false);
    const [emailCreditsError, setEmailCreditsError] = useState('');
    const [emailRegistrationCredits, setEmailRegistrationCredits] = useState(5);
    const [claimedInCurrentSession, setClaimedInCurrentSession] = useState(false); // Track if claimed in this session

    // Load email reward status
    const loadEmailRewardStatus = async () => {
        if (!user) {
            setIsLoadingEmailStatus(false);
            return;
        }
        
        setIsLoadingEmailStatus(true);
        try {
            const getCreditRewardStatusFunc = httpsCallable(functions, 'getCreditRewardStatus');
            const result = await getCreditRewardStatusFunc();
            if (result.data.email.claimed) {
                setEmailRewardStatus({
                    claimed: true,
                    email: result.data.email.email,
                    claimedAt: result.data.email.claimedAt
                });
            }
        } catch (error) {
            console.error("Error loading email reward status:", error);
        } finally {
            setIsLoadingEmailStatus(false);
        }
    };

    // Load email registration credits setting
    const loadEmailRegistrationCreditsSetting = async () => {
        try {
            const getSettingFunc = httpsCallable(functions, 'getEmailRegistrationCreditsSetting');
            const result = await getSettingFunc();
            if (result.data?.credits) {
                setEmailRegistrationCredits(result.data.credits);
            }
        } catch (error) {
            console.error("Error loading email registration credits setting:", error);
            // Keep default value of 5
        }
    };

    // Helper function to validate email
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Handle email credits submission
    const handleEmailCreditsSubmit = async (e) => {
        e.preventDefault();
        setEmailCreditsError('');
        
        if (!isValidEmail(emailForCredits)) {
            setEmailCreditsError(t('emailReg.invalidEmail'));
            return;
        }
        
        setIsSubmittingEmailForCredits(true);
        
        try {
            if (!user && onStravaConnect) {
                // For non-authenticated users, redirect to Strava connect with email in state
                localStorage.setItem('pendingEmailCredits', emailForCredits);
                onStravaConnect();
                return;
            }
            
            const claimEmailCreditsFunc = httpsCallable(functions, 'claimEmailRegistrationCredits');
            const result = await claimEmailCreditsFunc({ email: emailForCredits });
            
            if (result.data.success) {
                setEmailCreditsSuccess(true);
                setEmailRewardStatus({
                    claimed: true,
                    email: emailForCredits,
                    claimedAt: new Date()
                });
                setClaimedInCurrentSession(true); // Mark as claimed in current session
                
                // Call the callback if provided
                if (onRewardClaimed) {
                    onRewardClaimed();
                }
                
                posthog?.capture('email_credits_claimed', {
                    email: emailForCredits,
                    credits_earned: result.data.creditsAwarded || emailRegistrationCredits,
                    component: 'EmailRegistrationCard',
                    variant: variant
                });
            } else {
                setEmailCreditsError(result.data.error || t('emailReg.claimFailed'));
            }
        } catch (error) {
            console.error('Error claiming email credits:', error);
            setEmailCreditsError(t('emailReg.claimFailedTryAgain'));
            
            posthog?.capture('email_credits_error', {
                error_message: error.message,
                component: 'EmailRegistrationCard',
                variant: variant
            });
        } finally {
            setIsSubmittingEmailForCredits(false);
        }
    };

    // Load data on mount and when user changes
    useEffect(() => {
        loadEmailRewardStatus();
        loadEmailRegistrationCreditsSetting();
    }, [user]);

    // Don't render if loading
    if (isLoadingEmailStatus) {
        return null;
    }

    // Hide the card if already claimed and not claimed in current session (unless alwaysVisible is true)
    if (!alwaysVisible && emailRewardStatus.claimed && !claimedInCurrentSession) {
        return null; // Hide the component on page load/change if already claimed
    }

    // Get title and description based on variant and claimed status
    const getContent = () => {
        if (emailRewardStatus.claimed) {
            return {
                title: t('emailReg.thankYouTitle'),
                description: t('emailReg.thankYouDesc', { credits: emailRegistrationCredits })
            };
        }
        
        switch (variant) {
            case 'join':
                return {
                    title: t('emailReg.joinTitle'),
                    description: t('emailReg.joinDesc', { credits: emailRegistrationCredits })
                };
            case 'generation':
                return {
                    title: t('emailReg.getCreditsTitle', { count: emailRegistrationCredits }),
                    description: t('emailReg.newsletterDesc')
                };
            default:
                return {
                    title: t('emailReg.getCreditsTitle', { count: emailRegistrationCredits }),
                    description: t('emailReg.newsletterDesc')
                };
        }
    };

    const content = getContent();
    const buttonText = variant === 'join' ? 
        t('emailReg.getStartedCta', { credits: emailRegistrationCredits }) : 
        t('emailReg.claimCta', { count: emailRegistrationCredits });

    return (
        <div className={`email-registration-card ${emailRewardStatus.claimed ? 'claimed' : ''}`} style={style}>
            <div className="email-registration-content">
                <h4>{content.title}</h4>
                <p>{content.description}</p>
                
                <form onSubmit={emailRewardStatus.claimed ? undefined : handleEmailCreditsSubmit} className="email-registration-form">
                    <input
                        type="email"
                        placeholder={emailRewardStatus.claimed ? emailRewardStatus.email : t('common.enterEmail')}
                        value={emailRewardStatus.claimed ? emailRewardStatus.email : emailForCredits}
                        onChange={emailRewardStatus.claimed ? undefined : (e) => setEmailForCredits(e.target.value)}
                        disabled={emailRewardStatus.claimed || isSubmittingEmailForCredits}
                        required
                        readOnly={emailRewardStatus.claimed}
                    />
                    <button
                        type="submit"
                        disabled={emailRewardStatus.claimed || isSubmittingEmailForCredits || !emailForCredits || !isValidEmail(emailForCredits)}
                        className="email-registration-button"
                    >
                        {emailRewardStatus.claimed ? 
                            t('emailReg.claimed') :
                            (isSubmittingEmailForCredits ? 
                                (variant === 'join' ? t('emailReg.redirecting') : t('emailReg.claiming')) : 
                                buttonText
                            )
                        }
                    </button>
                    {emailCreditsError && !emailRewardStatus.claimed && (
                        <div className="email-registration-error">
                            <i className="ti ti-alert-circle" style={{ fontSize: '14px' }}></i>
                            {emailCreditsError}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default EmailRegistrationCard; 